/**
 * permitRowColors.ts — loads the lab's Permit Row Colors rules
 * (GET /api/v1/permit-row-colors) for the result grids and paints their colours.
 *
 * Every result form (Result Entry Express, Result Tracking, Result Validation /
 * Approval, SDMS) calls `usePermitRowColors()` once: the rules load once per session,
 * the form re-renders when they arrive or change, and `permitRowClassFor`
 * (permitLimit.ts) then classes each row with the rule it meets. Each rule's colour
 * is written into ONE generated <style> element (cells painted with !important, the
 * selected row and required-empty cells keep their own colour).
 *
 * The table is the ONLY source of row colours (owner decision 2026-10-23 — no built-in
 * fallback). If the rules cannot be loaded, rows keep the grid's default colour and the
 * next form that opens tries again.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { api } from '../../api/client';
import {
  getPermitRowColorVersion, permitRuleClass, setPermitRowColorRules, subscribePermitRowColors,
  type RowColorRule,
} from './permitRowColorState';

export const PERMIT_ROW_COLORS_URL = '/api/v1/permit-row-colors';

/** An operator a condition may use (Utility ▸ Permit ▸ Operators, Numeric rows). */
export interface RowColorOperatorOption { value: string; name: string; display: string }
/** A limit type a condition may name ("MDL" = the row's own MDL, always first). */
export interface RowColorLimitOption { id: string; name: string }
/** Everything the settings page and the grids need, in one read. */
export interface RowColorSet {
  rules: RowColorRule[];
  operators: RowColorOperatorOption[];
  limitTypes: RowColorLimitOption[];
}

/** AI_Operator StoredValue → the glyph shown in a rule. */
export const ROW_COLOR_OP_GLYPH: Record<string, string> = {
  LessThan: '<', LessThanEqual: '≤', GreaterThen: '>', GreaterThenEqual: '≥', Equal: '=', NotEqual: '≠',
};

/**
 * A rule written the way the lab writes it — "If result ≥ MRL, AL, SMCL, SMAL, < MCL".
 * A list in brackets means ALL of those limits ("< (MRL, AL)" = below both); a plain list
 * means ANY of them. The same wording the server writes (PermitRowColorRules.Describe), so
 * the page, the list and the audit trail read alike.
 */
export function describeRowColorRule(conditions: RowColorRule['conditions']): string {
  return 'If result ' + conditions.map((c) => {
    const names = c.limits.map((l) => l.name ?? '(deleted)').join(', ');
    const list = c.match === 'all' && c.limits.length > 1 ? `(${names})` : names;
    return `${ROW_COLOR_OP_GLYPH[c.op] ?? c.op} ${list}`;
  }).join(', ');
}

const STYLE_ID = 'plim-rule-colors';
const HEX = /^#[0-9a-fA-F]{6}$/;

/** The stylesheet for the given rules — exported for the tests. */
export function permitRuleCss(rules: RowColorRule[]): string {
  return rules
    .filter((r) => HEX.test(r.color))
    .map((r) => {
      const c = permitRuleClass(r);
      return [
        `.adg-table tbody tr.${c}:not(.adg-selected) > td:not(.adg-req-empty),`,
        `.adg-table tbody tr.${c}:not(.adg-selected) > td.adg-sticky:not(.adg-req-empty) { background: ${r.color} !important; }`,
        `.dv-tbl tbody tr.${c} > td, .dv-tbl tbody tr.${c} > td.dv-chk,`,
        `.dv-tbl tbody tr.${c} > td.dv-sys-col { background: ${r.color} !important; }`,
      ].join('\n');
    })
    .join('\n');
}

function paint(rules: RowColorRule[]): void {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = permitRuleCss(rules);
}

const DRAFT_STYLE_ID = 'plim-rule-colors-draft';

/**
 * Paint ONE unsaved rule (the settings page's New / Edit preview) with its own colour, in a
 * second <style> element so the saved rules are untouched; null removes it. The rule must
 * carry an id no saved rule uses (the page passes "draft").
 */
export function paintDraftPermitRule(rule: RowColorRule | null): void {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(DRAFT_STYLE_ID) as HTMLStyleElement | null;
  if (!rule) { el?.remove(); return; }
  if (!el) {
    el = document.createElement('style');
    el.id = DRAFT_STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = permitRuleCss([rule]);
}

/** Apply a freshly read rule set to every grid (also used by the settings page after a save). */
export function applyPermitRowColors(set: RowColorSet): void {
  paint(set.rules);
  setPermitRowColorRules(set.rules);
}

let inFlight: Promise<RowColorSet | null> | null = null;
let loaded = false;

/** Read the rule set. Errors are left to the caller (the settings page shows them). */
export async function fetchPermitRowColors(): Promise<RowColorSet> {
  const res = await api.get(PERMIT_ROW_COLORS_URL, { silentLoading: true } as never);
  return res.data.data as RowColorSet;
}

/** Load the rules once per session (or again with `force`). A failure keeps the built-in
 *  tiers and is retried by the next form that opens. */
export function loadPermitRowColors(force = false): Promise<RowColorSet | null> {
  if (loaded && !force) return Promise.resolve(null);
  if (inFlight) return inFlight;
  inFlight = fetchPermitRowColors()
    .then((set) => {
      if (!set || !Array.isArray(set.rules)) return null;   // unexpected shape → rows stay default
      applyPermitRowColors(set);
      loaded = true;
      return set;
    })
    // Not fatal: rows stay in their default colour and the next form that opens retries.
    .catch(() => null)
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** Test hook: forget the loaded rules. */
export function resetPermitRowColorsForTests(): void {
  loaded = false;
  inFlight = null;
  setPermitRowColorRules(null);
}

/** Call once in a result form: loads the rules and re-renders the form when they change. */
export function usePermitRowColors(): number {
  useEffect(() => { void loadPermitRowColors(); }, []);
  return useSyncExternalStore(subscribePermitRowColors, getPermitRowColorVersion, getPermitRowColorVersion);
}
